import { useEffect, useState, useCallback, useRef, memo, useMemo } from "react";
import {
    useToast, Box, Text, Grid, GridItem, Heading, Button, Input,
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter,
    ModalBody, ModalCloseButton, useDisclosure, Select, Switch,
    Flex, Tag, TagLabel, TagLeftIcon, Badge, Tooltip,
    HStack, VStack, Divider, IconButton, Card, CardBody, CardHeader,
    SimpleGrid, Image, Spinner, useColorModeValue,
    FormControl, FormLabel,
    Drawer, DrawerBody, DrawerHeader, DrawerOverlay, DrawerContent, DrawerCloseButton,
    Checkbox // <-- ADICIONADO
} from "@chakra-ui/react";
import {
    AddIcon,
    CheckCircleIcon,
    WarningIcon,
    SettingsIcon,
    DeleteIcon,
    RepeatIcon,
    SearchIcon,
    EditIcon,
    ChevronLeftIcon,
    ChevronRightIcon
} from "@chakra-ui/icons";
import { FaCrop, FaFilter } from 'react-icons/fa';

const VEHICLE_TYPES = [
    { id: 'CARRO', label: 'Carro' },
    { id: 'MOTO', label: 'Moto' },
    { id: 'ÔNIBUS', label: 'Ônibus' },
    { id: 'CAMINHÃO', label: 'Caminhão' },
    { id: 'BICICLETA', label: 'Bicicleta' }
];

// Gerenciador global de requisições de imagens
const ImageRequestQueue = (() => {
    const queue = [];
    const active = new Set();
    const MAX_CONCURRENT = 2; // Reduzido de 3 para 2 para diminuir carga no backend
    
    const processQueue = () => {
        while (queue.length > 0 && active.size < MAX_CONCURRENT) {
            const request = queue.shift();
            active.add(request.id);
            
            request.fetch()
                .finally(() => {
                    active.delete(request.id);
                    processQueue();
                });
        }
    };
    
    return {
        add: (id, fetchFn, priority = 0) => {
            // Evitar duplicatas
            if (active.has(id) || queue.some(item => item.id === id)) return;
            
            // Adicionar com prioridade (menor número = maior prioridade)
            const request = { id, fetch: fetchFn, priority };
            
            // Inserir na posição correta baseado na prioridade
            const insertIndex = queue.findIndex(item => item.priority > priority);
            if (insertIndex === -1) {
                queue.push(request);
            } else {
                queue.splice(insertIndex, 0, request);
            }
            
            processQueue();
        },
        clear: () => {
            queue.length = 0;
        },
        getQueueSize: () => queue.length,
        getActiveCount: () => active.size
    };
})();

// ADICIONE ESTE HELPER AQUI
const isAlprAlgorithm = (algorithm) => {
    if (!algorithm) return false;
    const name = (algorithm.name || '').toLowerCase();
    // aceita variações comuns e também uma possível chave 'alpr_infraction'
    if (name.includes('alpr') || name.includes('plate')) return true;
    if (algorithm.key && algorithm.key === 'alpr_infraction') return true;
    return false;
};

const AuthenticatedImage = memo(({ cameraId, alt, onRefresh, useLastImage = false, cameraIndex = 0, ...props }) => {
    const [imageUrl, setImageUrl] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const hasFetchedRef = useRef(false);
    const imgContainerRef = useRef(null);
    const failedAttemptsRef = useRef(0);
    const isLoadingRef = useRef(false);
    const isMountedRef = useRef(true);
    const isVisibleRef = useRef(false);
    const MAX_RETRY_ATTEMPTS = 1;

    const fetchImage = useCallback(async () => {
        // Evitar múltiplas chamadas simultâneas ou excesso de tentativas
        if (isLoadingRef.current || !isMountedRef.current || (hasFetchedRef.current && failedAttemptsRef.current >= MAX_RETRY_ATTEMPTS)) {
            return;
        }
        
        isLoadingRef.current = true;
        setLoading(true);

        try {
            setError(false);

            // Escolher endpoint baseado no prop useLastImage
            const endpoint = useLastImage
                ? `/api/cameras/last-image/${cameraId}`
                : `/api/cameras/still-image/${cameraId}`;

            const token = localStorage.getItem('token');
            const response = await fetch(endpoint, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Cache-Control': 'public, max-age=600'
                }
            });

            if (!response.ok) {
                failedAttemptsRef.current += 1;
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const blob = await response.blob();
            
            if (!isMountedRef.current) return;
            
            const url = URL.createObjectURL(blob);
            setImageUrl(url);
            setError(false);
            hasFetchedRef.current = true;
            failedAttemptsRef.current = 0;

        } catch (error) {
            if (!isMountedRef.current) return;
            console.error(`Error fetching camera ${cameraId} image (attempt ${failedAttemptsRef.current}):`, error.message);
            setError(true);
        } finally {
            isLoadingRef.current = false;
            if (isMountedRef.current) {
                setLoading(false);
            }
        }
    }, [cameraId, useLastImage]);

    // Função para refresh manual
    const handleRefresh = useCallback(() => {
        // Reset contadores para permitir nova tentativa manual
        hasFetchedRef.current = false;
        failedAttemptsRef.current = 0;
        isLoadingRef.current = false;
        setError(false);
        fetchImage();
    }, [fetchImage]);

    // IntersectionObserver: só carregar imagem quando o card está visível
    useEffect(() => {
        isMountedRef.current = true;
        const container = imgContainerRef.current;
        if (!container) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !isVisibleRef.current) {
                    isVisibleRef.current = true;
                    if (!hasFetchedRef.current && failedAttemptsRef.current < MAX_RETRY_ATTEMPTS) {
                        ImageRequestQueue.add(`camera-${cameraId}`, () => fetchImage(), cameraIndex);
                    }
                }
            },
            { rootMargin: '100px', threshold: 0.1 } // Começar a carregar 100px antes de ficar visível
        );

        observer.observe(container);

        return () => {
            isMountedRef.current = false;
            observer.disconnect();
        };
    }, [cameraId, cameraIndex, fetchImage]);

    // Registrar função de refresh apenas uma vez
    useEffect(() => {
        if (onRefresh) {
            onRefresh(handleRefresh);
        }
    }, [onRefresh, handleRefresh]);

    if (loading) {
        return (
            <Flex
                ref={imgContainerRef}
                justifyContent="center"
                alignItems="center"
                width="100%"
                height={props.height || "120px"}
                bg="gray.100"
                borderRadius="md"
            >
                <Spinner size="md" color="blue.500" />
            </Flex>
        );
    }

    if (error || !imageUrl) {
        return (
            <Box ref={imgContainerRef}>
                <Image
                    src={process.env.PUBLIC_URL + "/camera-offline.jpg"}
                    alt={alt || "Câmera offline"}
                    {...props}
                />
            </Box>
        );
    }

    return (
        <Box ref={imgContainerRef}>
            <Image src={imageUrl} alt={alt} {...props} />
        </Box>
    );
});

// Implementação corrigida do RoiSelector
const RoiSelector = memo(({ cameraId, onSave, onCancel }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [imageData, setImageData] = useState(null);
    const [roi, setRoi] = useState({ x: 0, y: 0, width: 100, height: 100 });
    const [existingRoi, setExistingRoi] = useState(null);
    const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
    const [imageLoaded, setImageLoaded] = useState(false);
    const imgRef = useRef(null);
    const toast = useToast();

    // Buscar configuração ROI existente e carregar imagem
    useEffect(() => {
        const fetchExistingRoi = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await fetch(`/api/cameras/roi/${cameraId}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (response.ok) {
                    const data = await response.json();
                    setExistingRoi(data);
                    setRoi({
                        x: data.x || 0,
                        y: data.y || 0,
                        width: data.width || 100,
                        height: data.height || 100
                    });
                }
            } catch (error) {
                console.error('Erro ao buscar ROI existente:', error);
            }
        };

        const loadImage = async () => {
            try {
                setIsLoading(true);
                const token = localStorage.getItem('token');

                const response = await fetch(`/api/cameras/still-image/${cameraId}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (response.ok) {
                    const blob = await response.blob();
                    const imageUrl = URL.createObjectURL(blob);
                    setImageData(imageUrl);
                } else {
                    throw new Error('Falha ao carregar imagem');
                }
            } catch (error) {
                console.error('Erro ao carregar imagem:', error);
                toast({
                    title: 'Erro ao carregar imagem',
                    description: 'Não foi possível carregar a imagem da câmera',
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                });
            } finally {
                setIsLoading(false);
            }
        };

        fetchExistingRoi();
        loadImage();

        return () => {
            if (imageData) {
                URL.revokeObjectURL(imageData);
            }
        };
    }, [cameraId]); // Apenas cameraId como dependência

    // Lidar com o carregamento da imagem
    const handleImageLoad = useCallback(() => {
        if (imgRef.current) {
            const { naturalWidth, naturalHeight } = imgRef.current;
            setImageSize({ width: naturalWidth, height: naturalHeight });
            setImageLoaded(true);

            // Se não há ROI existente, definir valores padrão baseados no tamanho da imagem
            if (!existingRoi) {
                setRoi({
                    x: Math.floor(naturalWidth * 0.25),
                    y: Math.floor(naturalHeight * 0.25),
                    width: Math.floor(naturalWidth * 0.5),
                    height: Math.floor(naturalHeight * 0.5)
                });
            }
        }
    }, [existingRoi]);

    // Funções para ajustar ROI com controles
    const adjustRoi = useCallback((property, value) => {
        if (!imageSize.width || !imageSize.height) return;

        setRoi(prev => {
            const updated = { ...prev };
            updated[property] += value;

            // Aplicar limites
            if (property === 'x') {
                updated.x = Math.max(0, Math.min(imageSize.width - updated.width, updated.x));
            } else if (property === 'y') {
                updated.y = Math.max(0, Math.min(imageSize.height - updated.height, updated.y));
            } else if (property === 'width') {
                updated.width = Math.max(10, Math.min(imageSize.width - updated.x, updated.width));
            } else if (property === 'height') {
                updated.height = Math.max(10, Math.min(imageSize.height - updated.y, updated.height));
            }

            return updated;
        });
    }, [imageSize]);

    // Funções para controles de tamanho e posição
    const moveLeft = useCallback(() => adjustRoi('x', -10), [adjustRoi]);
    const moveRight = useCallback(() => adjustRoi('x', 10), [adjustRoi]);
    const moveUp = useCallback(() => adjustRoi('y', -10), [adjustRoi]);
    const moveDown = useCallback(() => adjustRoi('y', 10), [adjustRoi]);
    const increaseWidth = useCallback(() => adjustRoi('width', 10), [adjustRoi]);
    const decreaseWidth = useCallback(() => adjustRoi('width', -10), [adjustRoi]);
    const increaseHeight = useCallback(() => adjustRoi('height', 10), [adjustRoi]);
    const decreaseHeight = useCallback(() => adjustRoi('height', -10), [adjustRoi]);

    // Função para atualizar ROI via inputs
    const handleRoiInputChange = useCallback((e) => {
        const { name, value } = e.target;
        const numValue = parseInt(value, 10);

        if (!isNaN(numValue)) {
            setRoi(prev => {
                const updated = { ...prev, [name]: numValue };

                // Aplicar restrições
                if (name === 'x') {
                    updated.x = Math.max(0, Math.min(imageSize.width - updated.width, updated.x));
                } else if (name === 'y') {
                    updated.y = Math.max(0, Math.min(imageSize.height - updated.height, updated.y));
                } else if (name === 'width') {
                    updated.width = Math.max(10, Math.min(imageSize.width - updated.x, updated.width));
                } else if (name === 'height') {
                    updated.height = Math.max(10, Math.min(imageSize.height - updated.y, updated.height));
                }

                return updated;
            });
        }
    }, [imageSize]);

    const handleSave = useCallback(() => {
        const naturalWidth = imgRef.current?.naturalWidth || imageSize.width;
        const naturalHeight = imgRef.current?.naturalHeight || imageSize.height;

        const roiWithDimensions = {
            x: roi.x,
            y: roi.y,
            width: roi.width,
            height: roi.height,
            sourceWidth: naturalWidth,
            sourceHeight: naturalHeight
        };

        console.log("✅ Salvando ROI:", roiWithDimensions);
        console.log("   Natural:", naturalWidth, "x", naturalHeight);
        console.log("   Rendered:", imgRef.current?.width, "x", imgRef.current?.height);

        onSave(roiWithDimensions);
    }, [roi, imageSize, onSave]);

    const handleReset = useCallback(() => {
        if (existingRoi) {
            setRoi({
                x: existingRoi.x,
                y: existingRoi.y,
                width: existingRoi.width,
                height: existingRoi.height
            });
        } else if (imageSize.width && imageSize.height) {
            setRoi({
                x: Math.floor(imageSize.width * 0.25),
                y: Math.floor(imageSize.height * 0.25),
                width: Math.floor(imageSize.width * 0.5),
                height: Math.floor(imageSize.height * 0.5)
            });
        }
    }, [existingRoi, imageSize]);

    // Calcular posição e tamanho do ROI em porcentagem
    const roiStyle = useMemo(() => {
        if (!imageLoaded || !imageSize.width || !imageSize.height) {
            return { display: 'none' };
        }

        return {
            position: 'absolute',
            top: `${(roi.y / imageSize.height) * 100}%`,
            left: `${(roi.x / imageSize.width) * 100}%`,
            width: `${(roi.width / imageSize.width) * 100}%`,
            height: `${(roi.height / imageSize.height) * 100}%`,
            border: '2px solid #38A169',
            backgroundColor: 'rgba(56, 161, 105, 0.2)',
            zIndex: 10,
            pointerEvents: 'none'
        };
    }, [roi, imageSize, imageLoaded]);

    return (
        <Box>
            {isLoading ? (
                <Flex justifyContent="center" alignItems="center" height="400px">
                    <Spinner size="xl" thickness="4px" color="blue.500" />
                </Flex>
            ) : (
                <Box>
                    <VStack spacing={6} align="stretch">
                        {/* Visualização da imagem com ROI */}
                        <Box
                            position="relative"
                            border="1px solid"
                            borderColor="gray.200"
                            borderRadius="md"
                            overflow="hidden"
                            bg="gray.100"
                        >
                            {imageData && (
                                <>
                                    <Image
                                        ref={imgRef}
                                        src={imageData}
                                        alt="Imagem da câmera"
                                        width="100%"
                                        style={{ display: "block" }}
                                        onLoad={handleImageLoad}
                                        onError={(e) => {
                                            console.error('Erro ao carregar imagem no ROI selector');
                                            toast({
                                                title: 'Erro na imagem',
                                                description: 'Não foi possível exibir a imagem',
                                                status: 'error',
                                                duration: 3000,
                                                isClosable: true,
                                            });
                                        }}
                                    />

                                    {/* Retângulo de ROI */}
                                    <div style={roiStyle} />
                                </>
                            )}
                        </Box>

                        {/* Informações da imagem */}
                        {imageLoaded && (
                            <Box p={3} bg="gray.50" borderRadius="md">
                                <Text fontSize="sm" color="gray.600">
                                    Tamanho da imagem: {imageSize.width} x {imageSize.height} px
                                </Text>
                                <Text fontSize="sm" color="green.600">
                                    ROI: {roi.x}, {roi.y} - {roi.width}x{roi.height} px
                                </Text>
                            </Box>
                        )}

                        {/* Controles e informações */}
                        <Grid templateColumns="repeat(2, 1fr)" gap={6}>
                            {/* Coluna de coordenadas */}
                            <GridItem>
                                <VStack spacing={4} align="stretch">
                                    <Heading size="sm">Coordenadas do ROI</Heading>

                                    <SimpleGrid columns={2} spacing={4}>
                                        <FormControl>
                                            <FormLabel>X</FormLabel>
                                            <Input
                                                name="x"
                                                value={roi.x}
                                                onChange={handleRoiInputChange}
                                                type="number"
                                                min={0}
                                                max={imageSize.width - roi.width}
                                            />
                                        </FormControl>

                                        <FormControl>
                                            <FormLabel>Y</FormLabel>
                                            <Input
                                                name="y"
                                                value={roi.y}
                                                onChange={handleRoiInputChange}
                                                type="number"
                                                min={0}
                                                max={imageSize.height - roi.height}
                                            />
                                        </FormControl>

                                        <FormControl>
                                            <FormLabel>Largura</FormLabel>
                                            <Input
                                                name="width"
                                                value={roi.width}
                                                onChange={handleRoiInputChange}
                                                type="number"
                                                min={10}
                                                max={imageSize.width - roi.x}
                                            />
                                        </FormControl>

                                        <FormControl>
                                            <FormLabel>Altura</FormLabel>
                                            <Input
                                                name="height"
                                                value={roi.height}
                                                onChange={handleRoiInputChange}
                                                type="number"
                                                min={10}
                                                max={imageSize.height - roi.y}
                                            />
                                        </FormControl>
                                    </SimpleGrid>
                                </VStack>
                            </GridItem>

                            {/* Coluna de controles */}
                            <GridItem>
                                <VStack spacing={4} align="stretch">
                                    <Heading size="sm">Controles</Heading>

                                    <Box borderWidth="1px" borderRadius="md" p={3}>
                                        <Text fontSize="sm" mb={2}>Posição</Text>
                                        <SimpleGrid columns={3} spacing={2} mb={4}>
                                            <Box></Box>
                                            <Button size="sm" onClick={moveUp} isDisabled={!imageLoaded}>
                                                ↑
                                            </Button>
                                            <Box></Box>

                                            <Button size="sm" onClick={moveLeft} isDisabled={!imageLoaded}>
                                                ←
                                            </Button>
                                            <Box></Box>
                                            <Button size="sm" onClick={moveRight} isDisabled={!imageLoaded}>
                                                →
                                            </Button>

                                            <Box></Box>
                                            <Button size="sm" onClick={moveDown} isDisabled={!imageLoaded}>
                                                ↓
                                            </Button>
                                            <Box></Box>
                                        </SimpleGrid>

                                        <Text fontSize="sm" mb={2}>Tamanho</Text>
                                        <SimpleGrid columns={2} spacing={2}>
                                            <HStack>
                                                <Text fontSize="sm" width="80px">Largura:</Text>
                                                <Button size="sm" onClick={decreaseWidth} isDisabled={!imageLoaded}>-</Button>
                                                <Button size="sm" onClick={increaseWidth} isDisabled={!imageLoaded}>+</Button>
                                            </HStack>

                                            <HStack>
                                                <Text fontSize="sm" width="80px">Altura:</Text>
                                                <Button size="sm" onClick={decreaseHeight} isDisabled={!imageLoaded}>-</Button>
                                                <Button size="sm" onClick={increaseHeight} isDisabled={!imageLoaded}>+</Button>
                                            </HStack>
                                        </SimpleGrid>
                                    </Box>
                                </VStack>
                            </GridItem>
                        </Grid>

                        <Divider />

                        {/* Botões de ação */}
                        <HStack spacing={4} justify="flex-end">
                            <Button
                                colorScheme="blue"
                                onClick={handleSave}
                                isDisabled={!imageLoaded}
                            >
                                Salvar ROI
                            </Button>

                            <Button
                                variant="outline"
                                onClick={handleReset}
                                isDisabled={!imageLoaded}
                            >
                                Redefinir
                            </Button>

                            <Button
                                variant="ghost"
                                onClick={onCancel}
                            >
                                Cancelar
                            </Button>
                        </HStack>
                    </VStack>
                </Box>
            )}
        </Box>
    );
});

const CAMERAS_PER_PAGE = 12; // Paginação para evitar renderizar centenas de cards

const Cameras = () => {
    const [cameras, setCameras] = useState([]);
    const [filteredCameras, setFilteredCameras] = useState([]);
    const [machines, setMachines] = useState([]);
    const [selectedCamera, setSelectedCamera] = useState({ algorithms: [] });
    const [algorithms, setAlgorithms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshFunctions, setRefreshFunctions] = useState({});
    const [isRoiModalOpen, setIsRoiModalOpen] = useState(false);
    const [roiCamera, setRoiCamera] = useState(null);

    // Estado para armazenar metadados das imagens (carregados em batch)
    const [imageMetadata, setImageMetadata] = useState({});

    // Paginação
    const [currentPage, setCurrentPage] = useState(1);

    // Estados para estatísticas
    const [statistics, setStatistics] = useState({
        total: 0,
        online: 0,
        offline: 0,
        totalActiveAlgorithms: 0
    });

    // Estados para filtros
    const [filters, setFilters] = useState({
        name: "",
        machineId: "",
        location: "",
        algorithmId: "",
        status: ""
    });

    const { isOpen, onOpen, onClose } = useDisclosure();
    const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
    const { isOpen: isFilterOpen, onOpen: onFilterOpen, onClose: onFilterClose } = useDisclosure();

    const [newCamera, setNewCamera] = useState({
        name: '',
        location: '',
        rtsp_address: '',
        machine_id: '',
        vehicle_types: [] // <-- ADICIONADO
    });
    const toast = useToast();

    const bgColor = useColorModeValue("white", "gray.800");
    const borderColor = useColorModeValue("gray.200", "gray.700");

    useEffect(() => {
        fetchCameras();
        fetchAlgorithms();
        fetchMachines();
    }, []);

    // Aplicar filtros e calcular estatísticas sempre que os dados mudarem
    useEffect(() => {
        applyFilters();
        calculateStatistics();
    }, [cameras, filters]);

    const fetchCameras = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            // Usar novo endpoint otimizado
            const response = await fetch('/api/cameras/with-services', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error('Erro ao buscar câmeras');
            const data = await response.json();
            setCameras(data);

            // Buscar metadados em batch (1 requisição em vez de N)
            fetchBatchMetadata(token);
        } catch (error) {
            toast({
                title: "Erro ao buscar câmeras",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    // Buscar metadados de todas as câmeras em uma única requisição
    const fetchBatchMetadata = async (token) => {
        try {
            const response = await fetch('/api/cameras/batch-image-metadata', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setImageMetadata(data);
            }
        } catch (error) {
            console.warn('Erro ao buscar metadados batch:', error);
        }
    };

    const handleOpenRoiModal = useCallback((camera) => {
        console.log('Abrindo modal ROI para câmera:', camera.id);
        setRoiCamera(camera);
        setIsRoiModalOpen(true);
    }, []);

    const handleCloseRoiModal = useCallback(() => {
        setIsRoiModalOpen(false);
        // Limpe o roiCamera apenas após o fechamento do modal
        setTimeout(() => {
            setRoiCamera(null);
        }, 300); // Dê tempo para a animação de fechamento terminar
    }, []);

    const handleSaveRoi = async (roi) => {
        if (!roiCamera) return;

        try {
            const token = localStorage.getItem('token');

            const response = await fetch(`/api/cameras/roi/${roiCamera.id}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(roi)
            });

            if (!response.ok) {
                throw new Error('Erro ao salvar configuração de ROI');
            }

            toast({
                title: "ROI configurado",
                description: "Área de interesse configurada com sucesso",
                status: "success",
                duration: 3000,
                isClosable: true,
            });

            handleCloseRoiModal();
        } catch (error) {
            console.error('Erro:', error);
            toast({
                title: "Erro",
                description: "Não foi possível configurar a área de interesse",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const fetchMachines = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/machines', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error('Erro ao buscar máquinas');
            const data = await response.json();
            setMachines(data);
        } catch (error) {
            console.error('Erro:', error);
        }
    };

    const fetchAlgorithms = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/algorithms', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error('Erro ao buscar algoritmos');
            const data = await response.json();
            setAlgorithms(data);
        } catch (error) {
            console.error('Erro:', error);
        }
    };

    // Função para calcular estatísticas
    const calculateStatistics = useCallback(() => {
        const total = cameras.length;
        const online = cameras.filter(camera => camera.isActive).length;
        const offline = total - online;
        
        // Somar total de algoritmos ativos
        const totalActiveAlgorithms = cameras.reduce((sum, camera) => {
            return sum + (camera.activeServices?.length || 0);
        }, 0);

        setStatistics({
            total,
            online,
            offline,
            totalActiveAlgorithms
        });
    }, [cameras]);

    // Função para aplicar filtros
    const applyFilters = () => {
        let result = [...cameras];

        // Filtro por nome da câmera
        if (filters.name) {
            result = result.filter(camera =>
                camera.name &&
                camera.name.toLowerCase().includes(filters.name.toLowerCase())
            );
        }

        // Filtro por máquina
        if (filters.machineId) {
            const machineIdNum = parseInt(filters.machineId, 10);
            result = result.filter(camera => camera.machine_id === machineIdNum);
        }

        // Filtro por localização (zona)
        if (filters.location) {
            result = result.filter(camera =>
                camera.location &&
                camera.location.toLowerCase().includes(filters.location.toLowerCase())
            );
        }

        // Filtro por status (ativa/inativa)
        if (filters.status) {
            if (filters.status === 'active') {
                result = result.filter(camera => camera.isActive);
            } else if (filters.status === 'inactive') {
                result = result.filter(camera => !camera.isActive);
            }
        }

        // Filtro por algoritmo ativo
        if (filters.algorithmId) {
            const algorithmIdNum = parseInt(filters.algorithmId, 10);
            result = result.filter(camera => {
                return camera.activeServices?.some(service => service.algorithm_id === algorithmIdNum);
            });
        }

        setFilteredCameras(result);
        // Resetar para página 1 quando filtros mudam
        setCurrentPage(1);
    };

    // Função para lidar com mudanças nos filtros
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    // Função para resetar filtros
    const resetFilters = () => {
        setFilters({
            name: "",
            machineId: "",
            location: "",
            algorithmId: "",
            status: ""
        });
    };

    // Função para contar câmeras filtradas
    const getFilteredCount = () => {
        return filteredCameras.length;
    };

    // Função para verificar se há filtros ativos
    const hasActiveFilters = () => {
        return filters.name || filters.machineId || filters.location || filters.algorithmId || filters.status;
    };

    const handleOpenModal = () => {
        onOpen();
        fetchMachines();
    };

    const handleOpenEditModal = async (camera) => {
        try {
            setSelectedCamera({ 
                ...camera, 
                algorithms: camera.activeServices?.map(s => s.algorithm_id) || [],
                vehicle_types: camera.vehicle_types || [] 
            });

            await fetchAlgorithms();
            await fetchMachines();

            onEditOpen();
        } catch (error) {
            console.error('Erro ao abrir o modal de edição:', error);
        }
    };

    const handleNewCameraInputChange = (e) => {
        const { name, value } = e.target;
        const parsedValue = name === 'machine_id' ? parseInt(value, 10) : value;
        setNewCamera(prev => ({ ...prev, [name]: parsedValue }));
    };

    // Novo: toggle para tipos de veículos no modal de adicionar
    const handleNewCameraToggleVehicleType = (typeId) => {
        setNewCamera(prev => {
            const current = new Set(prev.vehicle_types || []);
            if (current.has(typeId)) current.delete(typeId);
            else current.add(typeId);
            return { ...prev, vehicle_types: Array.from(current) };
        });
    };

    const handleSelectedCameraInputChange = (e) => {
        const { name, value } = e.target;
        const parsedValue = name === 'machine_id' ? parseInt(value, 10) : value;
        setSelectedCamera(prev => ({ ...prev, [name]: parsedValue }));
    };

    // Toggle para tipos de veículo no modal de edição
    const handleToggleSelectedCameraVehicleType = (typeId) => {
        setSelectedCamera(prev => {
            const list = Array.isArray(prev.vehicle_types) ? [...prev.vehicle_types] : [];
            const idx = list.indexOf(typeId);
            if (idx >= 0) list.splice(idx, 1);
            else list.push(typeId);
            return { ...prev, vehicle_types: list };
        });
    };

    const handleAddCamera = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/cameras', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(newCamera),
            });

            if (!response.ok) {
                throw new Error('Erro ao adicionar câmera');
            }

            const addedCamera = await response.json();
            setCameras(prev => [...prev, addedCamera]);
            setNewCamera({ name: '', location: '', rtsp_address: '', vehicle_types: [] });
            onClose();

            toast({
                title: "Câmera adicionada",
                description: "A nova câmera foi adicionada com sucesso",
                status: "success",
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            console.error('Erro:', error);
            toast({
                title: "Erro",
                description: "Não foi possível adicionar a câmera",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const handleUpdateCamera = async () => {
        try {
            const token = localStorage.getItem('token');
            const originalMachineId = cameras.find(c => c.id === selectedCamera.id)?.machine_id;

            const cameraResponse = await fetch(`/api/cameras/${selectedCamera.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(selectedCamera),
            });

            if (!cameraResponse.ok) {
                throw new Error(`Erro ao atualizar câmera: ${cameraResponse.statusText}`);
            }

            const updatedCamera = await cameraResponse.json();

            if (originalMachineId !== selectedCamera.machine_id) {
                const servicesResponse = await fetch(`/api/camera-services/${selectedCamera.id}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                if (!servicesResponse.ok) {
                    throw new Error('Erro ao buscar serviços da câmera');
                }

                const services = await servicesResponse.json();

                for (const service of services) {
                    const serviceResponse = await fetch(`/api/camera-services/${service.id}`, {
                        method: 'PUT',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify({
                            camera_id: service.camera_id,
                            machine_id: selectedCamera.machine_id,
                            algorithm_id: service.algorithm_id,
                            status: service.status
                        })
                    });

                    if (!serviceResponse.ok) {
                        const errorData = await serviceResponse.json();
                        throw new Error(`Erro ao atualizar serviço: ${errorData.details || errorData.message}`);
                    }
                }
            }

            setCameras(prev => prev.map(camera =>
                camera.id === updatedCamera.id ? updatedCamera : camera
            ));

            // Recarregar câmeras com serviços atualizados
            fetchCameras();

            toast({
                title: "Sucesso",
                description: "Câmera atualizada com sucesso",
                status: "success",
                duration: 3000,
                isClosable: true,
            });

            onEditClose();
        } catch (error) {
            console.error('Erro:', error);
            toast({
                title: "Erro",
                description: "Não foi possível atualizar a câmera",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const handleAlgorithmToggle = async (algorithmId) => {
        const isActive = selectedCamera.algorithms?.includes(algorithmId);
        try {
            const token = localStorage.getItem('token');

            if (isActive) {
                const serviceResponse = await fetch(`/api/camera-services/find`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        camera_id: selectedCamera.id,
                        algorithm_id: algorithmId
                    })
                });

                if (!serviceResponse.ok) throw new Error('Erro ao buscar serviço');
                const serviceData = await serviceResponse.json();

                const response = await fetch(`/api/camera-services/${serviceData.id}`, {
                    method: 'DELETE',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (!response.ok) throw new Error('Erro ao desativar algoritmo');
            } else {
                const response = await fetch('/api/camera-services', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        camera_id: selectedCamera.id,
                        machine_id: selectedCamera.machine_id,
                        algorithm_id: algorithmId,
                        status: true
                    }),
                });

                if (!response.ok) throw new Error('Erro ao ativar algoritmo');
            }

            setSelectedCamera((prev) => ({
                ...prev,
                algorithms: isActive
                    ? prev.algorithms.filter(id => id !== algorithmId)
                    : [...(prev.algorithms || []), algorithmId]
            }));
        } catch (error) {
            console.error('Erro:', error);
            toast({
                title: "Erro",
                description: "Não foi possível atualizar o algoritmo",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const handleDeleteCamera = async (cameraId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`/api/cameras/${cameraId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) throw new Error('Erro ao excluir câmera');

            setCameras(cameras.filter(camera => camera.id !== cameraId));

            toast({
                title: "Câmera excluída",
                description: "A câmera foi excluída com sucesso",
                status: "success",
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            console.error('Erro:', error);
            toast({
                title: "Erro",
                description: "Não foi possível excluir a câmera",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
    };

    // Função para obter o nome da máquina pelo ID
    const getMachineName = (machineId) => {
        const machine = machines.find(m => m.id === machineId);
        return machine ? machine.name : 'Máquina não encontrada';
    };

    // Função para obter o nome do algoritmo pelo ID
    const getAlgorithmName = (algorithmId) => {
        const algorithm = algorithms.find(a => a.id === algorithmId);
        return algorithm ? algorithm.name : 'Algoritmo não encontrado';
    };

    const getAlgorithmIcon = (algorithmId) => {
        switch (algorithmId) {
            case 1: return "👤";
            case 2: return "🚗";
            case 3: return "🧵";
            case 4: return "📱";
            case 5: return "⛑️";
            case 6: return "🛑";
            default: return "🔍";
        }
    };

    // Usar ref para evitar re-renders desnecessários
    const refreshFunctionsRef = useRef({});

    const handleSetRefreshFunction = useCallback((cameraId, refreshFn) => {
        refreshFunctionsRef.current[cameraId] = refreshFn;
        setRefreshFunctions(prev => ({
            ...prev,
            [cameraId]: refreshFn
        }));
    }, []);

    // Função para refresh manual
    const refreshCameraImage = useCallback((cameraId) => {
        if (refreshFunctions[cameraId]) {
            refreshFunctions[cameraId]();
        }
    }, [refreshFunctions]);

    return (
        <Box p={6} flex="1" minH="100vh" bg="gray.100" justify="center">
            <Flex justify="space-between" align="center" mb={6}>
                <Heading>Câmeras</Heading>
                <HStack spacing={4}>
                    {/* Estatísticas */}
                    <HStack spacing={3} bg="white" p={3} borderRadius="md" boxShadow="sm">
                        <Tooltip label="Total de câmeras" placement="top">
                            <HStack spacing={1}>
                                <Badge colorScheme="blue" fontSize="md" px={2} py={1}>
                                    📹 {statistics.total}
                                </Badge>
                            </HStack>
                        </Tooltip>
                        
                        <Divider orientation="vertical" height="20px" />
                        
                        <Tooltip label="Câmeras online (com algoritmos ativos)" placement="top">
                            <HStack spacing={1}>
                                <Badge colorScheme="green" fontSize="md" px={2} py={1}>
                                    ✅ {statistics.online}
                                </Badge>
                            </HStack>
                        </Tooltip>
                        
                        <Tooltip label="Câmeras offline (sem algoritmos ativos)" placement="top">
                            <HStack spacing={1}>
                                <Badge colorScheme="red" fontSize="md" px={2} py={1}>
                                    ⭕ {statistics.offline}
                                </Badge>
                            </HStack>
                        </Tooltip>
                        
                        <Divider orientation="vertical" height="20px" />
                        
                        <Tooltip label="Total de algoritmos ativos em todas as câmeras" placement="top">
                            <HStack spacing={1}>
                                <Badge colorScheme="purple" fontSize="md" px={2} py={1}>
                                    🔍 {statistics.totalActiveAlgorithms}
                                </Badge>
                            </HStack>
                        </Tooltip>
                    </HStack>
                    
                    <Button
                        onClick={onFilterOpen}
                        colorScheme="blue"
                        size="md"
                        leftIcon={<FaFilter />}
                        variant={hasActiveFilters() ? "solid" : "outline"}
                    >
                        Filtros
                        {hasActiveFilters() && (
                            <Badge ml={2} colorScheme="white" color="blue.500">
                                {Object.values(filters).filter(Boolean).length}
                            </Badge>
                        )}
                    </Button>
                    <Button
                        onClick={handleOpenModal}
                        colorScheme="teal"
                        size="md"
                        leftIcon={<AddIcon />}
                    >
                        Adicionar Câmera
                    </Button>
                </HStack>
            </Flex>

            {/* Informações sobre filtros aplicados */}
            {hasActiveFilters() && (
                <Box mb={4} p={4} bg="blue.50" borderRadius="md" borderWidth="1px" borderColor="blue.200">
                    <Flex justify="space-between" align="center">
                        <HStack spacing={4} wrap="wrap">
                            <Text fontWeight="medium" color="blue.700">
                                Filtros ativos:
                            </Text>
                            {filters.name && (
                                <Tag colorScheme="blue" size="sm">
                                    Nome: {filters.name}
                                </Tag>
                            )}
                            {filters.machineId && (
                                <Tag colorScheme="blue" size="sm">
                                    Máquina: {getMachineName(parseInt(filters.machineId))}
                                </Tag>
                            )}
                            {filters.location && (
                                <Tag colorScheme="blue" size="sm">
                                    Zona: {filters.location}
                                </Tag>
                            )}
                            {filters.algorithmId && (
                                <Tag colorScheme="blue" size="sm">
                                    Algoritmo: {getAlgorithmName(parseInt(filters.algorithmId))}
                                </Tag>
                            )}
                            {filters.status && (
                                <Tag colorScheme="blue" size="sm">
                                    Status: {filters.status === 'active' ? 'Ativas' : filters.status === 'inactive' ? 'Inativas' : 'Todas'}
                                </Tag>
                            )}
                        </HStack>
                        <HStack>
                            <Text fontSize="sm" color="blue.600">
                                {getFilteredCount()} de {cameras.length} câmeras
                            </Text>
                            <Button size="sm" variant="ghost" colorScheme="blue" onClick={resetFilters}>
                                Limpar
                            </Button>
                        </HStack>
                    </Flex>
                </Box>
            )}

            {loading ? (
                <Flex justify="center" align="center" height="300px">
                    <Spinner size="xl" thickness="4px" speed="0.65s" color="teal.500" />
                </Flex>
            ) : filteredCameras.length === 0 ? (
                <Flex direction="column" align="center" justify="center" py={12} bg="white" borderRadius="md" boxShadow="sm">
                    <Text fontSize="xl" mb={4}>
                        {hasActiveFilters() ? 'Nenhuma câmera encontrada com os filtros aplicados' : 'Nenhuma câmera cadastrada'}
                    </Text>
                    {hasActiveFilters() ? (
                        <Button colorScheme="blue" onClick={resetFilters}>Limpar Filtros</Button>
                    ) : (
                        <Button colorScheme="teal" onClick={handleOpenModal}>Adicionar Primeira Câmera</Button>
                    )}
                </Flex>
            ) : (
                <>
                    {/* Dica sobre carregamento lazy */}
                    <Box mb={4} p={3} bg="blue.50" borderRadius="md" borderWidth="1px" borderColor="blue.100">
                        <HStack spacing={2}>
                            <Text fontSize="sm" color="blue.700">
                                ℹ️ <strong>Otimização ativa:</strong> As imagens das câmeras são carregadas apenas quando visíveis na tela, reduzindo o uso de recursos.
                            </Text>
                        </HStack>
                    </Box>

                    <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 4 }} spacing={6}>
                    {(() => {
                        const totalPages = Math.ceil(filteredCameras.length / CAMERAS_PER_PAGE);
                        const startIndex = (currentPage - 1) * CAMERAS_PER_PAGE;
                        const paginatedCameras = filteredCameras.slice(startIndex, startIndex + CAMERAS_PER_PAGE);
                        return paginatedCameras;
                    })().map((camera, index) => {
                        const isActive = camera.isActive;
                        const activeServices = camera.activeServices || [];
                        const metadata = imageMetadata[camera.id];

                        return (
                            <Box
                                key={camera.id}
                                bg={bgColor}
                                p={4}
                                borderRadius="md"
                                boxShadow="md"
                                borderWidth="1px"
                                borderColor={borderColor}
                                position="relative"
                                _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
                                transition="all 0.2s"
                            >
                                {/* Status Badge */}
                                <Badge
                                    position="absolute"
                                    top={2}
                                    right={2}
                                    colorScheme={isActive ? "green" : "red"}
                                    fontSize="xs"
                                    px={2}
                                    py={1}
                                    borderRadius="full"
                                    zIndex={1}
                                >
                                    {isActive ? "Ativa" : "Inativa"}
                                </Badge>

                                {/* Camera Image */}
                                <Box mb={4} position="relative">
                                    <AuthenticatedImage
                                        cameraId={camera.id}
                                        cameraIndex={index}
                                        alt={`Câmera ${camera.name}`}
                                        width="100%"
                                        height="130px"
                                        objectFit="cover"
                                        borderRadius="md"
                                        useLastImage={true}
                                        onRefresh={(refreshFn) => handleSetRefreshFunction(camera.id, refreshFn)}
                                    />

                                    {/* Refresh Button */}
                                    <IconButton
                                        icon={<RepeatIcon />}
                                        size="sm"
                                        position="absolute"
                                        bottom={2}
                                        right={2}
                                        colorScheme="blue"
                                        variant="solid"
                                        onClick={() => refreshCameraImage(camera.id)}
                                        aria-label="Atualizar imagem"
                                        opacity={0.8}
                                        _hover={{ opacity: 1 }}
                                    />
                                </Box>

                                {/* Camera Info */}
                                <VStack align="start" spacing={2}>
                                    <Heading size="sm" noOfLines={1}>
                                        {camera.name}
                                    </Heading>

                                    <Text fontSize="sm" color="gray.600" noOfLines={2}>
                                        {camera.location}
                                    </Text>

                                    <Text fontSize="xs" color="gray.500">
                                        Máquina: {getMachineName(camera.machine_id)}
                                    </Text>

                                    {/* Última captura timestamp */}
                                    <Text fontSize="xs" color="gray.500">
                                        Última captura: {
                                            metadata?.timestamp ?
                                                new Date(metadata.timestamp).toLocaleString('pt-BR', {
                                                    day: '2-digit',
                                                    month: '2-digit',
                                                    year: 'numeric',
                                                    hour: '2-digit',
                                                    minute: '2-digit',
                                                    second: '2-digit'
                                                }) :
                                                camera.last_capture_time ?
                                                    new Date(camera.last_capture_time).toLocaleString('pt-BR', {
                                                        day: '2-digit',
                                                        month: '2-digit',
                                                        year: 'numeric',
                                                        hour: '2-digit',
                                                        minute: '2-digit',
                                                        second: '2-digit'
                                                    }) :
                                                    'Sem registro'
                                        }
                                    </Text>

                                    {/* Active Services */}
                                    {activeServices.length > 0 && (
                                        <HStack spacing={1} flexWrap="wrap">
                                            {activeServices.map(service => (
                                                <Badge
                                                    key={service.algorithm_id}
                                                    colorScheme="blue"
                                                    fontSize="xs"
                                                    px={2}
                                                    py={1}
                                                >
                                                    {getAlgorithmIcon(service.algorithm_id)} {getAlgorithmName(service.algorithm_id)}
                                                </Badge>
                                            ))}
                                        </HStack>
                                    )}
                                </VStack>

                                {/* Action Buttons */}
                                <HStack spacing={2} mt={4}>
                                    <IconButton
                                        icon={<EditIcon />}
                                        size="sm"
                                        colorScheme="blue"
                                        variant="outline"
                                        onClick={() => handleOpenEditModal(camera)}
                                        aria-label="Editar câmera"
                                    />

                                    <IconButton
                                        icon={<SettingsIcon />}
                                        size="sm"
                                        colorScheme="green"
                                        variant="outline"
                                        onClick={() => handleOpenRoiModal(camera)}
                                        aria-label="Configurar ROI"
                                    />

                                    <IconButton
                                        icon={<DeleteIcon />}
                                        size="sm"
                                        colorScheme="red"
                                        variant="outline"
                                        onClick={() => handleDeleteCamera(camera.id)}
                                        aria-label="Deletar câmera"
                                    />
                                </HStack>
                            </Box>
                        );
                    })}
                </SimpleGrid>

                    {/* Controles de Paginação */}
                    {filteredCameras.length > CAMERAS_PER_PAGE && (() => {
                        const totalPages = Math.ceil(filteredCameras.length / CAMERAS_PER_PAGE);
                        return (
                            <Flex justify="center" align="center" mt={6} gap={2}>
                                <IconButton
                                    icon={<ChevronLeftIcon />}
                                    size="sm"
                                    onClick={() => { setCurrentPage(p => Math.max(1, p - 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                                    isDisabled={currentPage === 1}
                                    aria-label="Página anterior"
                                />
                                
                                {Array.from({ length: totalPages }, (_, i) => i + 1)
                                    .filter(page => {
                                        // Mostrar primeira, última, e páginas próximas da atual
                                        return page === 1 || page === totalPages || Math.abs(page - currentPage) <= 2;
                                    })
                                    .reduce((acc, page, idx, arr) => {
                                        // Adicionar "..." entre páginas não consecutivas
                                        if (idx > 0 && page - arr[idx - 1] > 1) {
                                            acc.push(<Text key={`ellipsis-${page}`} fontSize="sm" color="gray.400" px={1}>...</Text>);
                                        }
                                        acc.push(
                                            <Button
                                                key={page}
                                                size="sm"
                                                variant={currentPage === page ? "solid" : "outline"}
                                                colorScheme={currentPage === page ? "blue" : "gray"}
                                                onClick={() => { setCurrentPage(page); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                                                minW="36px"
                                            >
                                                {page}
                                            </Button>
                                        );
                                        return acc;
                                    }, [])
                                }

                                <IconButton
                                    icon={<ChevronRightIcon />}
                                    size="sm"
                                    onClick={() => { setCurrentPage(p => Math.min(totalPages, p + 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                                    isDisabled={currentPage === totalPages}
                                    aria-label="Próxima página"
                                />

                                <Text fontSize="sm" color="gray.500" ml={3}>
                                    Página {currentPage} de {totalPages} ({filteredCameras.length} câmeras)
                                </Text>
                            </Flex>
                        );
                    })()}
                </>
            )}

            {/* Drawer de Filtros */}
            <Drawer isOpen={isFilterOpen} placement="right" onClose={onFilterClose} size="sm">
                <DrawerOverlay />
                <DrawerContent>
                    <DrawerCloseButton />
                    <DrawerHeader borderBottomWidth="1px">
                        <Flex align="center">
                            <FaFilter style={{ marginRight: '8px' }} />
                            Filtrar Câmeras
                        </Flex>
                    </DrawerHeader>

                    <DrawerBody>
                        <VStack spacing={4} align="stretch" py={4}>
                            <FormControl>
                                <FormLabel>Nome da Câmera</FormLabel>
                                <Input
                                    name="name"
                                    placeholder="Digite o nome da câmera"
                                    value={filters.name}
                                    onChange={handleFilterChange}
                                    leftElement={<SearchIcon color="gray.400" />}
                                />
                            </FormControl>

                            <FormControl>
                                <FormLabel>Máquina</FormLabel>
                                <Select
                                    name="machineId"
                                    placeholder="Todas as máquinas"
                                    value={filters.machineId}
                                    onChange={handleFilterChange}
                                >
                                    {machines.map(machine => (
                                        <option key={machine.id} value={machine.id}>
                                            {machine.name} ({machine.ip_address})
                                        </option>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl>
                                <FormLabel>Zona/Localização</FormLabel>
                                <Input
                                    name="location"
                                    placeholder="Digite a zona ou localização"
                                    value={filters.location}
                                    onChange={handleFilterChange}
                                />
                            </FormControl>

                            <FormControl>
                                <FormLabel>Status da Câmera</FormLabel>
                                <Select
                                    name="status"
                                    placeholder="Todos os status"
                                    value={filters.status}
                                    onChange={handleFilterChange}
                                >
                                    <option value="active">Câmeras Ativas</option>
                                    <option value="inactive">Câmeras Inativas</option>
                                </Select>
                            </FormControl>

                            <FormControl>
                                <FormLabel>Algoritmo Ativo</FormLabel>
                                <Select
                                    name="algorithmId"
                                    placeholder="Todos os algoritmos"
                                    value={filters.algorithmId}
                                    onChange={handleFilterChange}
                                >
                                    {algorithms.map(algorithm => (
                                        <option key={algorithm.id} value={algorithm.id}>
                                            {getAlgorithmIcon(algorithm.id)} {algorithm.name}
                                        </option>
                                    ))}
                                </Select>
                            </FormControl>

                            <Divider />

                            <VStack spacing={3}>
                                <Button
                                    colorScheme="blue"
                                    width="full"
                                    onClick={onFilterClose}
                                    isDisabled={!hasActiveFilters()}
                                >
                                    Aplicar Filtros ({getFilteredCount()})
                                </Button>

                                <Button
                                    variant="outline"
                                    width="full"
                                    onClick={resetFilters}
                                    isDisabled={!hasActiveFilters()}
                                >
                                    Limpar Filtros
                                </Button>
                            </VStack>

                            {hasActiveFilters() && (
                                <Box mt={4} p={3} bg="gray.50" borderRadius="md">
                                    <Text fontSize="sm" fontWeight="medium" mb={2}>
                                        Filtros Ativos:
                                    </Text>
                                    <VStack spacing={1} align="stretch">
                                        {filters.name && (
                                            <Text fontSize="xs" color="gray.600">
                                                • Nome: {filters.name}
                                            </Text>
                                        )}
                                        {filters.machineId && (
                                            <Text fontSize="xs" color="gray.600">
                                                • Máquina: {getMachineName(parseInt(filters.machineId))}
                                            </Text>
                                        )}
                                        {filters.location && (
                                            <Text fontSize="xs" color="gray.600">
                                                • Zona: {filters.location}
                                            </Text>
                                        )}
                                        {filters.status && (
                                            <Text fontSize="xs" color="gray.600">
                                                • Status: {filters.status === 'active' ? 'Ativas' : 'Inativas'}
                                            </Text>
                                        )}
                                        {filters.algorithmId && (
                                            <Text fontSize="xs" color="gray.600">
                                                • Algoritmo: {getAlgorithmName(parseInt(filters.algorithmId))}
                                            </Text>
                                        )}
                                    </VStack>
                                    <Text fontSize="xs" color="blue.600" mt={2} fontWeight="medium">
                                        Mostrando {getFilteredCount()} de {cameras.length} câmeras
                                    </Text>
                                </Box>
                            )}
                        </VStack>
                    </DrawerBody>
                </DrawerContent>
            </Drawer>

            <Modal isOpen={isOpen} onClose={onClose}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Adicionar Nova Câmera</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <VStack spacing={4}>
                            <FormControl isRequired>
                                <FormLabel>Nome da Câmera</FormLabel>
                                <Input
                                    placeholder="Ex: Câmera Entrada Principal"
                                    name="name"
                                    value={newCamera.name}
                                    onChange={handleNewCameraInputChange}
                                />
                            </FormControl>

                            <FormControl>
                                <FormLabel>Localização</FormLabel>
                                <Input
                                    placeholder="Ex: Portaria Sul"
                                    name="location"
                                    value={newCamera.location}
                                    onChange={handleNewCameraInputChange}
                                />
                            </FormControl>

                            <FormControl isRequired>
                                <FormLabel>Endereço RTSP</FormLabel>
                                <Input
                                    placeholder="rtsp://user:senha@IP/streaming/channels/101"
                                    name="rtsp_address"
                                    value={newCamera.rtsp_address}
                                    onChange={handleNewCameraInputChange}
                                />
                            </FormControl>

                            <FormControl isRequired>
                                <FormLabel>Máquina</FormLabel>
                                <Select
                                    placeholder="Selecione a Máquina"
                                    name="machine_id"
                                    value={newCamera.machine_id || ''}
                                    onChange={handleNewCameraInputChange}
                                >
                                    {machines.map(machine => (
                                        <option key={machine.id} value={machine.id}>
                                            {machine.name}
                                        </option>
                                    ))}
                                </Select>
                            </FormControl>

                            {/* Novo: Tipos de Veículo */}
                            <FormControl>
                                <FormLabel>Tipos de Veículo (geram infração)</FormLabel>
                                <VStack align="start">
                                    {VEHICLE_TYPES.map(vt => (
                                        <Checkbox
                                            key={vt.id}
                                            isChecked={newCamera.vehicle_types?.includes(vt.id)}
                                            onChange={() => handleNewCameraToggleVehicleType(vt.id)}
                                        >
                                            {vt.label}
                                        </Checkbox>
                                    ))}
                                </VStack>
                            </FormControl>
                        </VStack>
                    </ModalBody>
                    <ModalFooter>
                        <Button colorScheme="teal" onClick={handleAddCamera} mr={3}>
                            Adicionar
                        </Button>
                        <Button variant="ghost" onClick={onClose}>
                            Cancelar
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* ROI Configuration Modal */}
            <Modal
                isOpen={isRoiModalOpen}
                onClose={handleCloseRoiModal}
                size="xl"
                closeOnOverlayClick={false}
                motionPreset="none" // Desativa a animação para evitar problemas de montagem
            >
                <ModalOverlay />
                <ModalContent maxW="80vw">
                    <ModalHeader bg="green.50" borderBottomWidth="1px" borderColor="green.100">
                        <Flex alignItems="center">
                            <Text>Configurar Área de Interesse (ROI)</Text>
                            <Badge ml={3} colorScheme="green">{roiCamera?.name}</Badge>
                        </Flex>
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody p={4}>
                        {roiCamera && (
                            <RoiSelector
                                key={`roi-${roiCamera.id}-${isRoiModalOpen}`} // Key dependente do estado de abertura do modal
                                cameraId={roiCamera.id}
                                onSave={handleSaveRoi}
                                onCancel={handleCloseRoiModal}
                            />
                        )}
                    </ModalBody>
                </ModalContent>
            </Modal>

            <Modal isOpen={isEditOpen} onClose={onEditClose} size="lg">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader bg="blue.50" borderBottomWidth="1px" borderColor="blue.100">
                        <Flex alignItems="center">
                            <Text>Editar Câmera</Text>
                            <Badge ml={3} colorScheme="blue">{selectedCamera?.name}</Badge>
                        </Flex>
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody p={6}>
                        <VStack spacing={4} align="stretch">
                            <FormControl isRequired>
                                <FormLabel>Nome da Câmera</FormLabel>
                                <Input
                                    name="name"
                                    value={selectedCamera?.name || ''}
                                    onChange={handleSelectedCameraInputChange}
                                />
                            </FormControl>

                            <FormControl>
                                <FormLabel>Localização</FormLabel>
                                <Input
                                    name="location"
                                    value={selectedCamera?.location || ''}
                                    onChange={handleSelectedCameraInputChange}
                                />
                            </FormControl>

                            <FormControl isRequired>
                                <FormLabel>Endereço RTSP</FormLabel>
                                <Input
                                    name="rtsp_address"
                                    value={selectedCamera?.rtsp_address || ''}
                                    onChange={handleSelectedCameraInputChange}
                                />
                            </FormControl>

                            <FormControl isRequired>
                                <FormLabel>Máquina</FormLabel>
                                <Select
                                    name="machine_id"
                                    value={selectedCamera?.machine_id || ''}
                                    onChange={handleSelectedCameraInputChange}
                                >
                                    {machines.map(machine => (
                                        <option key={machine.id} value={machine.id}>
                                            {machine.name} ({machine.ip_address})
                                        </option>
                                    ))}
                                </Select>
                            </FormControl>

                            <Box mt={4}>
                                <Heading size="md" mb={4}>Serviços Ativos</Heading>
                                <Divider mb={4} />

                                {algorithms.map(algorithm => (
                                    <Flex
                                        key={algorithm.id}
                                        alignItems="center"
                                        justifyContent="space-between"
                                        p={3}
                                        bg={selectedCamera?.algorithms?.includes(algorithm.id) ? "green.50" : "gray.50"}
                                        borderRadius="md"
                                        mb={2}
                                        direction="column"
                                    >
                                        <Flex width="100%" alignItems="center" justifyContent="space-between">
                                            <HStack>
                                                <Box mr={2} fontSize="lg">
                                                    {getAlgorithmIcon(algorithm.id)}
                                                </Box>
                                                <Text fontWeight="medium">{algorithm.name}</Text>
                                            </HStack>
                                            <Switch
                                                colorScheme="green"
                                                isChecked={selectedCamera?.algorithms?.includes(algorithm.id) || false}
                                                onChange={() => handleAlgorithmToggle(algorithm.id)}
                                            />
                                        </Flex>

                                        {/* Mostrar seletores de tipos de veículo somente para ALPR e somente quando ligado */}
                                        {isAlprAlgorithm(algorithm) && selectedCamera?.algorithms?.includes(algorithm.id) && (
                                            <Box mt={3} width="100%" bg="whiteAlpha.900" p={3} borderRadius="md" borderWidth="1px" borderColor="gray.100">
                                                <Text fontSize="sm" color="gray.600" mb={2}>
                                                    Tipos de veículo que geram infração para este algoritmo (ALPR).
                                                    Marque os tipos que devem gerar infração — tipos não marcados serão registrados apenas como passagem.
                                                </Text>
                                                <VStack align="start" spacing={1}>
                                                    {VEHICLE_TYPES.map(vt => (
                                                        <Checkbox
                                                            key={vt.id}
                                                            isChecked={selectedCamera?.vehicle_types?.includes(vt.id)}
                                                            onChange={() => handleToggleSelectedCameraVehicleType(vt.id)}
                                                            size="sm"
                                                        >
                                                            {vt.label}
                                                        </Checkbox>
                                                    ))}
                                                </VStack>
                                            </Box>
                                        )}
                                    </Flex>
                                ))}
                            </Box>
                        </VStack>
                    </ModalBody>
                    <ModalFooter borderTopWidth="1px" borderColor="gray.200">
                        <Button colorScheme="teal" mr={3} onClick={handleUpdateCamera}>
                            Salvar Alterações
                        </Button>
                        <Button variant="outline" onClick={onEditClose}>
                            Cancelar
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
};

export default Cameras;
